import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Divider,
  Chip,
  Avatar,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { blogService, getLastSource, getLastError } from '../../services/api/blogService';

function formatDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toISOString().slice(0, 10);
  } catch {
    return String(d);
  }
}

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState('loading');
  const [lastError, setLastError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [detail, list] = await Promise.all([
          blogService.getBlogById(id),
          blogService.listBlogs({ status: 'published', limit: 50 }).catch(() => [])
        ]);
        if (mounted) {
          setPost(detail);
          setAllBlogs(Array.isArray(list) ? list : []);
          setDataSource(getLastSource());
          setLastError(getLastError());
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  const relatedPosts = useMemo(() => {
    if (!post || !Array.isArray(allBlogs)) return [];
    const postTags = new Set(post.tags || []);
    return allBlogs
      .filter(b => String(b.id) !== String(post.id))
      .map(b => {
        let score = 0;
        (b.tags || []).forEach(t => { if (postTags.has(t)) score++; });
        return { ...b, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }, [post, allBlogs]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Blog post not found
        </Typography>
        <Button
          component={Link}
          to="/blog"
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Blog
        </Button>
      </Container>
    );
  }

  const seoDescription = post.seoDescription || post.excerpt || post.title;
  const canonical = `https://www.scrumtherapist.com/blog/${encodeURIComponent(post.id)}`;
  const imageUrl = post.image?.startsWith('http')
    ? post.image
    : `https://www.scrumtherapist.com${post.image || '/images/logo.png'}`;
  const dateStr = formatDate(post.date);

  return (
    <>
      <Helmet>
        <title>{`${post.title} | ScrumTherapist`}</title>
        <link rel="canonical" href={canonical} />
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={(post.keywords || post.tags || []).join(', ')} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "image": [imageUrl],
            "datePublished": dateStr,
            "dateModified": dateStr,
            "author": {
              "@type": "Person",
              "name": post.author || 'Jenny Johnson',
              "jobTitle": post.authorTitle || 'Certified Scrum Master & Agile Coach'
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonical
            },
            "publisher": {
              "@type": "Organization",
              "name": "ScrumTherapist",
              "url": "https://www.scrumtherapist.com/",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.scrumtherapist.com/images/logo.png"
              }
            },
            "description": seoDescription,
            "keywords": (post.keywords || post.tags || []).join(', '),
            "articleSection": (post.tags || []).slice(0, 3)
          })}
        </script>
      </Helmet>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {dataSource === 'fallback' && (
          <Box
            sx={{
              mb: 3,
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'warning.lighter',
              border: 1,
              borderColor: 'warning.light',
              color: 'warning.dark'
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              ⚠️ Showing Offline Sample Data — Could not reach blog backend
              {lastError?.status != null && <> (HTTP {String(lastError.status)})</>}
              . New AI articles will not appear until the backend is back online.
            </Typography>
          </Box>
        )}
        <Button
          component={Link}
          to="/blog"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 4 }}
        >
          Back to Blog
        </Button>

        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, mb: 6 }}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            {post.title}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {(post.tags || []).map(tag => (
              <Chip key={tag} label={tag} size="small" />
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar src={post.authorImage} alt={post.author} sx={{ mr: 2, width: 48, height: 48 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                {post.author || 'Jenny Johnson'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {post.authorTitle || 'Certified Scrum Master & Agile Coach'} • {dateStr}
              </Typography>
            </Box>
          </Box>

          <Box
            component="img"
            src={post.image}
            alt={post.title}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: 500,
              objectFit: 'cover',
              borderRadius: 2,
              mb: 4
            }}
          />

          <Box
            className="blog-content"
            sx={{
              '& h2': {
                mt: 4,
                mb: 2,
                fontWeight: 'bold',
                color: 'primary.main'
              },
              '& h3': {
                mt: 3,
                mb: 2,
                fontWeight: 'medium'
              },
              '& p': {
                mb: 2,
                lineHeight: 1.7
              },
              '& ul, & ol': {
                mb: 2,
                pl: 4
              },
              '& li': {
                mb: 1
              }
            }}
            dangerouslySetInnerHTML={{ __html: post.content || '' }}
          />
        </Paper>

        {relatedPosts.length > 0 && (
          <Box sx={{ mt: 8 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Related Articles
            </Typography>
            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={4}>
              {relatedPosts.map((relatedPost) => (
                <Grid item xs={12} sm={6} md={4} key={relatedPost.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 6,
                      }
                    }}
                  >
                    <CardActionArea component={Link} to={`/blog/${relatedPost.id}`}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={relatedPost.image}
                        alt={relatedPost.title}
                      />
                      <CardContent>
                        <Typography gutterBottom variant="h6" component="h2">
                          {relatedPost.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          {(relatedPost.tags || []).slice(0, 2).map(tag => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDate(relatedPost.date)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </>
  );
};

export default BlogPost;
